/**
 * 사업계획서 저장소 (IndexedDB + API 메타)
 * 키: tenantCode + fiscalYear + planId
 * HWP 바이트는 localStorage 한도를 넘기므로 IDB에 둔다.
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, parseJsonObject } from 'services/commonQuery';

const DB_NAME = 'erp-biz-plan';
const DB_VERSION = 1;
const STORE = 'plans';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'planId' });
        os.createIndex('byTenantYear', ['tenantCode', 'fiscalYear'], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function withStore(mode, fn) {
  return openDb().then(async (db) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const finished = new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('aborted'));
    });
    const result = await fn(store);
    await finished;
    db.close();
    return result;
  });
}

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function createPlanId(fiscalYear) {
  const y = String(fiscalYear || new Date().getFullYear());
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 8)
      : Date.now().toString(36);
  return `BP-${y}-${rand}`;
}

function toBytes(hwpBytes) {
  if (!hwpBytes) return null;
  if (hwpBytes instanceof ArrayBuffer) return new Uint8Array(hwpBytes);
  if (hwpBytes instanceof Uint8Array) return hwpBytes;
  return new Uint8Array(hwpBytes);
}

const BIZ_PLAN_SAVE_QUERY = [
  'BdBudgetExec.insertBizPlan',
  'BdBudgetExec.updateBizPlan',
  'BdBudgetExec.deleteBizPlan',
];

function mapBizPlanFromApi(row) {
  const fields = parseJsonObject(row.fieldsJson) || {};
  const draft = parseJsonObject(row.draftJson) || {};
  return {
    planId: row.planId,
    tenantCode: row.tenantCode || '',
    fiscalYear: String(row.fiscalYear || ''),
    formId: row.formId || '',
    formName: row.formName || '',
    fileName: row.fileName || '',
    title: row.title || '',
    status: row.status || 'DRAFT',
    picked: {
      deptCode: row.deptCode || '',
      deptName: row.deptName || '',
      projectCode: row.projectCode || '',
      projectName: row.projectName || '',
    },
    fields,
    draft,
    createdAt: row.createdAt || '',
    updatedAt: row.updatedAt || '',
  };
}

function bizPlanMetaPayload(rec) {
  const picked = rec.picked || {};
  return {
    planId: rec.planId,
    fiscalYear: rec.fiscalYear,
    formId: rec.formId || null,
    formName: rec.formName || null,
    fileName: rec.fileName || null,
    title: rec.title || null,
    status: rec.status || 'DRAFT',
    deptCode: picked.deptCode || rec.deptCode || null,
    deptName: picked.deptName || rec.deptName || null,
    projectCode: picked.projectCode || rec.projectCode || null,
    projectName: picked.projectName || rec.projectName || null,
    fieldsJson: JSON.stringify(rec.fields || {}),
    draftJson: JSON.stringify(rec.draft || {}),
  };
}

async function syncBizPlanMeta(rec, mod) {
  if (!isBdApiEnabled() || !rec?.planId) return;
  await commonSaveList(
    BIZ_PLAN_SAVE_QUERY,
    [{ ...bizPlanMetaPayload(rec), mod }],
    { tenantCode: rec.tenantCode, fiscalYear: rec.fiscalYear }
  );
}

async function loadBizPlanMetaFromApi({ tenantCode, fiscalYear, planId } = {}) {
  const rows = await commonSelect('BdBudgetExec.selectBizPlanList', {
    tenantCode,
    fiscalYear,
    planId,
  });
  return rows.length ? mapBizPlanFromApi(rows[0]) : null;
}

function metaOf(rec) {
  if (!rec) return null;
  const { hwpBytes, ...rest } = rec;
  return {
    ...rest,
    hasHwp: !!(hwpBytes && (hwpBytes.byteLength || hwpBytes.length)),
    hwpBytesLen: hwpBytes?.byteLength || hwpBytes?.length || 0,
  };
}

/** 목록 (바이트 제외) */
export async function listBizPlans({ tenantCode, fiscalYear } = {}) {
  const tc = String(tenantCode || '');
  const fy = String(fiscalYear || '');

  if (isBdApiEnabled() && tc) {
    const apiRows = (await commonSelect('BdBudgetExec.selectBizPlanList', { tenantCode: tc, fiscalYear: fy })).map(
      mapBizPlanFromApi
    );
    const idbAll = await withStore('readonly', (store) => idbReq(store.getAll()));
    const hwpById = new Map(
      (idbAll || []).map((r) => [r.planId, !!(r.hwpBytes && (r.hwpBytes.byteLength || r.hwpBytes.length))])
    );
    return apiRows
      .map((r) => ({
        ...r,
        hasHwp: hwpById.get(r.planId) || false,
        hwpBytesLen: 0,
      }))
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  }

  const all = await withStore('readonly', (store) => idbReq(store.getAll()));
  return (all || [])
    .filter((r) => (!tc || r.tenantCode === tc) && (!fy || String(r.fiscalYear) === fy))
    .map(metaOf)
    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
}

export async function getBizPlan(planId) {
  if (!planId) return null;
  const rec = await withStore('readonly', (store) => idbReq(store.get(planId)));
  if (rec) {
    return {
      ...rec,
      hwpBytes: toBytes(rec.hwpBytes),
    };
  }
  if (!isBdApiEnabled()) return null;
  const apiMeta = await loadBizPlanMetaFromApi({ planId });
  return apiMeta ? { ...apiMeta, hwpBytes: null } : null;
}

export async function upsertBizPlan(payload) {
  const now = new Date().toISOString();
  const planId = payload.planId || createPlanId(payload.fiscalYear);
  const existing = await getBizPlan(planId);
  const next = {
    planId,
    tenantCode: payload.tenantCode || existing?.tenantCode || '',
    fiscalYear: String(payload.fiscalYear || existing?.fiscalYear || ''),
    formId: payload.formId || existing?.formId || '',
    formName: payload.formName || existing?.formName || '',
    fileName: payload.fileName || existing?.fileName || '',
    title:
      payload.title ||
      payload.picked?.projectName ||
      payload.fields?.['doc.title'] ||
      existing?.title ||
      '',
    status: payload.status || existing?.status || 'DRAFT',
    picked: { ...(existing?.picked || {}), ...(payload.picked || {}) },
    fields: { ...(existing?.fields || {}), ...(payload.fields || {}) },
    draft: payload.draft != null ? payload.draft : existing?.draft || {},
    hwpBytes: payload.hwpBytes != null ? toBytes(payload.hwpBytes) : existing?.hwpBytes || null,
    createdAt: existing?.createdAt || payload.createdAt || now,
    updatedAt: now,
    savedAt: now,
  };
  await withStore('readwrite', (store) => {
    store.put(next);
  });
  if (isBdApiEnabled()) {
    const exists = await loadBizPlanMetaFromApi({
      tenantCode: next.tenantCode,
      fiscalYear: next.fiscalYear,
      planId: next.planId,
    });
    await syncBizPlanMeta(next, exists ? 'U' : 'A');
  }
  return metaOf(next);
}

export async function removeBizPlan(planId) {
  if (!planId) return false;
  const existing = await getBizPlan(planId);
  await withStore('readwrite', (store) => {
    store.delete(planId);
  });
  if (isBdApiEnabled() && existing?.planId) {
    await syncBizPlanMeta(existing, 'D');
  }
  return true;
}

/** 이관용: 지정 건 또는 당해 연도 최신 1건 */
export async function resolveBizPlanForImport({ tenantCode, fiscalYear, planId } = {}) {
  if (planId) {
    const one = await getBizPlan(planId);
    if (one) return one;
  }
  const list = await listBizPlans({ tenantCode, fiscalYear });
  if (!list.length) return null;
  return getBizPlan(list[0].planId);
}
